// ─── Auth Hooks Suite ─────────────────────────────────────────────────────────
// Story 4: Reusable authentication hooks using TanStack Query
// No business logic in components — all auth logic lives here

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import type { Location } from 'react-router-dom';

import authService from '../services/authService';
import { useAuthStore } from '../store/authStore';
import {
  ForgotPasswordPayload,
  LoginPayload,
  RegisterPayload,
  ResendVerificationPayload,
  ResetPasswordPayload,
  UserRole,
  VerifyEmailPayload,
} from '../types/auth.types';

// ─── useAuth ──────────────────────────────────────────────────────────────────
// Primary hook — exposes current auth state

export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const isStaff = useAuthStore((s) => s.isStaff);
  const status = useAuthStore((s) => s.status);
  const hasRole = useAuthStore((s) => s.hasRole);

  return {
    user,
    isAuthenticated,
    isAdmin,
    isStaff,
    status,
    hasRole,
    isLoading: status === 'loading' || status === 'idle',
  };
}

// ─── useLogin ─────────────────────────────────────────────────────────────────

export function useLogin() {
  const { setUser } = useAuthStore();
  // const navigate = useNavigate();
  // const location = useLocation();

  const mutation = useMutation({
    mutationFn: (payload: LoginPayload) => authService.login(payload),
    onSuccess: (response) => {
    setUser((response as any).data.user, (response as any).data.access);
    // useGuestOnly in LoginPage handles the redirect
  },
  });

  return {
    login: mutation.mutate,
    loginAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ─── useRegister ──────────────────────────────────────────────────────────────

export function useRegister() {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (payload: RegisterPayload) => authService.register(payload),
    onSuccess: (_data, variables) => {
      navigate('/auth/verify-email-pending', {
        state: { email: variables.email },
        replace: true,
      });
    },
  });

  return {
    register: mutation.mutate,
    registerAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ─── useLogout ────────────────────────────────────────────────────────────────

export function useLogout() {
  const { clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // Best-effort: clear local state regardless
    } finally {
      clearAuth();
      queryClient.clear();
      navigate('/auth/login', { replace: true });
    }
  }, [clearAuth, navigate, queryClient]);

  return { logout };
}

// ─── useRefreshToken ──────────────────────────────────────────────────────────

export function useRefreshToken() {
  const { setAccessToken, clearAuth, setStatus } = useAuthStore();

  const attemptRefresh = useCallback(async (): Promise<boolean> => {
    setStatus('loading');
    try {
      const token = await authService.refreshToken();
      setAccessToken(token);
      return true;
    } catch {
      clearAuth();
      return false;
    }
  }, [setAccessToken, clearAuth, setStatus]);

  return { attemptRefresh };
}

// ─── useForgotPassword ────────────────────────────────────────────────────────

export function useForgotPassword() {
  const mutation = useMutation({
    mutationFn: (payload: ForgotPasswordPayload) => authService.forgotPassword(payload),
  });

  return {
    submit: mutation.mutate,
    submitAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ─── useResetPassword ─────────────────────────────────────────────────────────

export function useResetPassword() {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (payload: ResetPasswordPayload) => authService.resetPassword(payload),
    onSuccess: () => {
      navigate('/auth/login', {
        state: { toast: { type: 'success', message: 'Password reset successfully. Please log in.' } },
        replace: true,
      });
    },
  });

  return {
    submit: mutation.mutate,
    submitAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset,
  };
}

// ─── useVerifyEmail ───────────────────────────────────────────────────────────

export function useVerifyEmail() {
  const mutation = useMutation({
    mutationFn: (payload: VerifyEmailPayload) => authService.verifyEmail(payload),
  });

  return {
    verify: mutation.mutate,
    verifyAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
}

// ─── useResendVerification ────────────────────────────────────────────────────

export function useResendVerification() {
  const mutation = useMutation({
    mutationFn: (payload: ResendVerificationPayload) => authService.resendVerification(payload),
  });

  return {
    resend: mutation.mutate,
    resendAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  };
}

// ─── useRequireAuth ───────────────────────────────────────────────────────────
// Redirect to login if not authenticated. Used in page-level guards.

export function useRequireAuth(requiredRole?: UserRole) {
  const { isAuthenticated, status, hasRole, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (status === 'idle' || status === 'loading') return;

    if (!isAuthenticated) {
      navigate('/auth/login', {
        replace: true,
        state: { returnTo: location.pathname + location.search },
      });
      return;
    }

    if (requiredRole && !hasRole(requiredRole)) {
      navigate(user?.role === 'customer' ? '/account' : '/admin', { replace: true });
    }
  }, [isAuthenticated, status, requiredRole, hasRole, navigate, location, user]);

  return { isAuthenticated, status };
}

// ─── useGuestOnly ─────────────────────────────────────────────────────────────
// Redirect authenticated users away from guest pages (login, register)

export function useGuestOnly(redirectTo = '/account') {
  const { isAuthenticated, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (status === 'idle' || status === 'loading') return;
    if (isAuthenticated) {
      const from = (location.state as { from?: Location })?.from;
      navigate(from?.pathname ?? redirectTo, { replace: true });
    }
  }, [isAuthenticated, status, redirectTo, navigate, location]);

  return { isAuthenticated, status };
}